/**
 * Sentry loader — runs ONLY after the user has explicitly accepted the cookie
 * banner. Session Replay records mouse moves + clicks and cannot be loaded
 * before consent. Mirrors posthogLoader.js so the whole consent lifecycle
 * (accept / reject / not-yet-decided) reads symmetrically.
 *
 * Lifecycle contract:
 *   loadSentry()   — called when user clicks "Accept All". Initialises
 *                    Sentry with browserTracing + replayIntegration.
 *   stopSentry()   — called when user clicks "Reject All" (mid-session) or
 *                    withdraws consent. Closes the current Sentry client so
 *                    no further errors, transactions or replays are sent.
 *
 * The DSN comes from REACT_APP_SENTRY_DSN. If it's missing we no-op — same
 * behaviour as the old index.js block.
 */
import * as Sentry from '@sentry/react';
import logger from '../logger';

let _loaded = false;

export function isSentryLoaded() {
  return _loaded;
}

export function loadSentry() {
  if (_loaded) return;
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) {
    logger.warn('Sentry DSN not configured — skipping load.');
    return;
  }
  try {
    Sentry.init({
      dsn,
      debug: false,
      integrations: [
        Sentry.browserTracingIntegration({ disableFetchRequestCloning: true }),
        Sentry.replayIntegration(),
      ],
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      environment: process.env.REACT_APP_ENVIRONMENT || 'production',
    });
    Sentry.getCurrentScope().setTag('cookie_consent', 'accepted');
    window.Sentry = Sentry;
    _loaded = true;
    logger.info('Sentry loaded after consent.');
  } catch (err) {
    logger.error('Sentry init failed', err);
  }
}

export function stopSentry() {
  try {
    const client = Sentry.getClient();
    if (client) {
      Sentry.getCurrentScope().setUser(null);
      Sentry.getCurrentScope().setTag('cookie_consent', 'rejected');
      // close() flushes any queued events then permanently disables the client
      // for this page. New init calls would be required to re-enable.
      client.close(1000).catch((e) => logger.warn('Sentry close error', e));
    }
    _loaded = false;
  } catch (err) {
    logger.warn('stopSentry failed silently', err);
  }
}
